import api from "@/api";
import { jwtDecode } from "jwt-decode";
import {
	getBasePageUrlFromRole,
	getLocalStorageReac,
	getSessionStorageReac,
	setLocalStorageReac,
	setSessionStorageReac,
} from "@/assets/js/helpers";
import { baseURL } from "@/api/config";
import type { TUserData } from "@/api/types";
import { useWebSocketStore } from "./websocket";
import { useProfileStore } from "./profile";

export const useSessionStore = defineStore("session", () => {
	const router = useRouter();
	const route = useRoute();
	const userToken = ref("");
	const oldToken = ref("");

	const oldTokenData = ref<TUserData | null>();
	const userData = ref<TUserData | null>();
	const tokenRefreshTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
	const isBusyRenewalToken = ref(false);
	const userId = computed(() => userData.value?.sub);

	const isLoggedIn = computed(() => !!userToken.value);
	const isUserAdmin = computed(
		() => userData.value?.roles?.includes(1) || false,
		// true,
	);
	const userPermissions = computed<string[]>(
		() => userData.value?.permissions || [],
	);

	const roleId = computed(() => userData.value?.roles?.[0]);
	const userImage = computed(() => {
		return userData.value ? getImageUrl(userData.value.img_id) : null;
	});
	function getImageUrl(imageId?: number | string): string | null {
		if (imageId) {
			return `${baseURL}/images/${imageId}`;
		}
		return null;
	}

	const isImpersonating = computed(() => !!oldToken.value);

	function cancelImpersonate() {
		if (oldToken.value) {
			saveToken(oldToken.value, true);
			oldToken.value = "";
			userData.value = oldTokenData.value;
			oldTokenData.value = null;
			sessionStorage.removeItem("user-token");
		}
	}

	function saveToken(token: string, isRemember = false, impersonate = false) {
		if (impersonate) {
			oldToken.value = userToken.value;
			oldTokenData.value = { ...userData.value };
		}
		userToken.value = token;
		userData.value = token ? jwtDecode(token) : null;

		if (token) {
			if (isRemember) {
				setLocalStorageReac("user-token", token);
			} else {
				setSessionStorageReac("user-token", token);
			}
			useWebSocketStore().initWs(token);
		} else {
			const sesToken = getSessionStorageReac("user-token");
			const locToken = getLocalStorageReac("user-token");
			sesToken.value = null;
			locToken.value = null;
		}

		const defaultPageForUser = getBasePageUrlFromRole(
			userData.value?.roles?.[0],
			true,
		);
		if (
			route &&
			route.matched.length > 0 &&
			!route.matched.some((record) => record.name === defaultPageForUser)
		) {
			router.push({ name: defaultPageForUser } as any);
		}
	}
	async function loadSessionToken() {
		const sesToken =
			(getSessionStorageReac("user-token").value as string) || "";
		const locToken = (getLocalStorageReac("user-token").value as string) || "";

		if (sesToken) {
			getSessionStorageReac("user-token").value = null;
			// await loadSession(sesToken, true);
			// console.log("session loaded");
		}

		const loadSession = async (token: string) => {
			if (typeof token === "string") {
				saveToken(token, true);
				if (!tokenRefreshTimeout.value) {
					await setupTokenRenewal(true);
				}
			} else {
				console.error("Invalid token format", token);
			}
		};

		await loadSession(locToken);
	}

	async function setupTokenRenewal(isRefreshToken = false, debug = true) {
		if (isBusyRenewalToken.value) {
			// console.warn(":: Busy token, exiting");
			return;
		}

		isBusyRenewalToken.value = true;
		tokenRefreshTimeout.value && clearTimeout(tokenRefreshTimeout.value);
		tokenRefreshTimeout.value = null;

		if (isRefreshToken) {
			await renewToken();
		}

		const TIME_TO_REFRESH = 15 * 60 * 1000;
		if (debug) {
			console.log(
				`:: Setup token refresh - ${TIME_TO_REFRESH / 60 / 1000}min`,
				isRefreshToken,
			);
		}
		tokenRefreshTimeout.value = setTimeout(() => {
			void setupTokenRenewal(true);
		}, TIME_TO_REFRESH);
		isBusyRenewalToken.value = false;
	}
	function cancelRenewToken() {
		if (tokenRefreshTimeout.value) {
			clearTimeout(tokenRefreshTimeout.value);
			tokenRefreshTimeout.value = null;
		}
	}
	async function renewToken() {
		if (!userToken.value && tokenRefreshTimeout.value) {
			return;
		}

		try {
			const res = await api.authRenewSession();
			const token = res.data.data.token;
			console.log(":: Token refreshed");
			saveToken(token, true);
		} catch {
			// ignored
		}
	}

	async function setupLogin(
		payload: Parameters<typeof api.authLogin>[0],
		isRemember = true,
	) {
		try {
			const res = await api.authLogin(payload);
			saveToken(res.data.data.token, true);
			return { val: true, res };
		} catch (err: any) {
			console.warn(err.message);
			return { val: false, err };
		}
	}
	async function logoutApi(
		showMsg = false,
		shouldRedirect = true,
	): Promise<boolean> {
		const checkRedirectUser = () => {
			// the route object is not define at this step leading to every page leading to auth which when user is not logged in causes pages like forget password and validation which should be able to be accessed without a session to redirect to login
			// globalThis.location.pathname.startsWith("/auth") works well but check for ways to refactor this to keep consistent with using vue-router
			if (shouldRedirect && !route.matched.some((r) => r.name === "auth")) {
				try {
					router.push({ name: "auth.login" });
				} catch (err) {
					console.error(err);
				}
			}
		};

		userData.value = null;
		useProfileStore().resetProfile();
		if (!userToken.value) {
			console.warn(":: User already logged out");
			// useToastStore().openToastWarning("User already logged out");
			checkRedirectUser();
			return false;
		}
		try {
			await api.authLogout();
			cancelRenewToken();

			checkRedirectUser();

			return true;
		} catch {
			return true;
		} finally {
			saveToken("", true);
		}
	}

	/**
	 * This function is used to check if the current user has specific permission
	 */
	function can(...keys: string[]) {
		if (keys.includes("admin") && isUserAdmin.value) {
			return true;
		}
		return keys.every((key) => userPermissions.value.includes(key));
	}

	return {
		isLoggedIn,
		isUserAdmin,
		userData,
		userImage,
		can,
		setupLogin,
		saveToken,
		loadSessionToken,
		setupTokenRenewal,
		logoutApi,
		roleId,
		getImageUrl,
		userId,
		cancelImpersonate,
		isImpersonating,
		renewToken,
	};
});
